/**
 * ConfirmDeleteModals — three compact delete confirmations (direct
 * chat, project, chat inside a project). Composes the shared
 * `<ConfirmDialog>` primitive so the visual language matches the rest
 * of the app's destructive flows.
 *
 * Phase E1 extraction, migrated to the unified confirm system.
 */

import type { ChatMetadata } from '../../../modules/chat/chatMetadataService';
import ConfirmDialog from '../../ui/ConfirmDialog';
import type { Project } from './types';

type Props = {
    directChats: ChatMetadata[];
    projects: Project[];
    confirmDeleteChatId: string | null;
    confirmDeleteProjectId: string | null;
    confirmDeleteProjectChatId: { projectId: string; chatId: string } | null;
    isDeletingChatId: string | null;
    isDeletingProjectId: string | null;
    isDeletingProjectChatId: string | null;
    onCancelChat: () => void;
    onCancelProject: () => void;
    onCancelProjectChat: () => void;
    onConfirmChat: (chatId: string) => void;
    onConfirmProject: (projectId: string) => void;
    onConfirmProjectChat: (projectId: string, chatId: string) => void;
};

export default function ConfirmDeleteModals({
    directChats, projects,
    confirmDeleteChatId, confirmDeleteProjectId, confirmDeleteProjectChatId,
    isDeletingChatId, isDeletingProjectId, isDeletingProjectChatId,
    onCancelChat, onCancelProject, onCancelProjectChat,
    onConfirmChat, onConfirmProject, onConfirmProjectChat,
}: Props) {
    const chatName = confirmDeleteChatId
        ? directChats.find((c) => c.id === confirmDeleteChatId)?.name ?? 'this chat'
        : null;
    const projectName = confirmDeleteProjectId
        ? projects.find((p) => p.id === confirmDeleteProjectId)?.name ?? 'this project'
        : null;
    const projectChatName = confirmDeleteProjectChatId
        ? projects
              .find((p) => p.id === confirmDeleteProjectChatId.projectId)
              ?.chats.find((c) => c.id === confirmDeleteProjectChatId.chatId)?.name
            ?? 'this chat'
        : null;

    return (
        <>
            <ConfirmDialog
                open={confirmDeleteChatId !== null}
                title="Delete chat?"
                description={
                    <>
                        <strong>{chatName}</strong> and its history will be
                        permanently removed. This cannot be undone.
                    </>
                }
                confirmLabel="Delete chat"
                destructive
                busy={isDeletingChatId === confirmDeleteChatId}
                onClose={onCancelChat}
                onConfirm={() => confirmDeleteChatId && onConfirmChat(confirmDeleteChatId)}
            />

            <ConfirmDialog
                open={confirmDeleteProjectId !== null}
                title="Delete project?"
                description={
                    <>
                        <strong>{projectName}</strong> and all its chats will be
                        permanently removed. This cannot be undone.
                    </>
                }
                confirmLabel="Delete project"
                destructive
                busy={isDeletingProjectId === confirmDeleteProjectId}
                onClose={onCancelProject}
                onConfirm={() => confirmDeleteProjectId && onConfirmProject(confirmDeleteProjectId)}
            />

            <ConfirmDialog
                open={confirmDeleteProjectChatId !== null}
                title="Delete chat?"
                description={
                    <>
                        <strong>{projectChatName}</strong> will be permanently
                        removed. This cannot be undone.
                    </>
                }
                confirmLabel="Delete chat"
                destructive
                busy={isDeletingProjectChatId === confirmDeleteProjectChatId?.chatId}
                onClose={onCancelProjectChat}
                onConfirm={() => {
                    if (confirmDeleteProjectChatId) {
                        onConfirmProjectChat(
                            confirmDeleteProjectChatId.projectId,
                            confirmDeleteProjectChatId.chatId,
                        );
                    }
                }}
            />
        </>
    );
}
